@if (auth.user$ | async; as user) {
  <mat-sidenav-container autosize class="sidenav-container">
    <mat-sidenav #drawer [attr.role]="(hs.isHandset$ | async) ? 'dialog' : 'navigation'"
                 [class.collapsed]="cs.collapseMenu$ | async"
                 [class.mobile]="hs.isHandset$ | async"
                 [mode]="(hs.isHandset$ | async) ? 'over' : 'side'"
                 [opened]="(hs.isHandset$ | async) === false"
                 class="sidenav"
                 fixedInViewport>
      <!-- C17: a flex column so the logo header stays fixed and ONLY the menu region
           below scrolls when it overflows (the whole sidebar used to scroll). -->
      <div class="flex flex-col h-full">
        <mat-toolbar class="relative !justify-between space-x-4 shrink-0">
          <div class="py-3 w-full h-full !pr-8">
            <img alt="RealGuest Logo" class="object-contain h-full"
                 src="{{environment.assetsBucketUrl}}/img/logo{{(cs.collapseMenu$ | async) ? '_small' : ''}}.png">
          </div>
        </mat-toolbar>

        <div class="flex-1 min-h-0 overflow-y-auto">
          @if (user.firstLogin) {
            <mat-nav-list>
              <div class="px-4">
                <h4 class="!mb-2"><strong>{{ 'core.navigation.profile' | translate }}</strong></h4>
                <p class="mat-body-small">{{ 'core.navigation.completeProfile' | translate }}</p>
                <button class="w-full my-2"
                        color="accent"
                        mat-raised-button routerLink="/profile">
                  {{ 'core.navigation.completeProfileAction' | translate }}
                </button>
              </div>
            </mat-nav-list>
            <mat-divider></mat-divider>
          }
          @if (cs.menuItems$ | async; as menuItems) {
            <mat-nav-list dense>
              @if (menuItems.common?.length ?? 0 > 0) {
                @for (m of menuItems.common; track (m.url ?? m.label)) {
                  <nbd-nav-menu-item
                    [collapsed]="(cs.collapseMenu$ | async) ?? false"
                    [drawer]="drawer"
                    [isHandset]="(hs.isHandset$ | async) ?? false"
                    [m]="m"></nbd-nav-menu-item>
                }
              }
              <!-- Recent events: quick links to the 5 last-opened events (expanded sidebar only). -->
              @if (!(cs.collapseMenu$ | async)) {
                @if (es.recentEvents$ | async; as recent) {
                  @if (recent.length > 0) {
                    <div class="nav-subheader">{{ 'core.navigation.recentEvents' | translate }}</div>
                    @for (ev of recent; track ev.id) {
                      <a class="nav-sub-item" [routerLink]="['/events', ev.id, 'overview']"
                         routerLinkActive="active">
                        <mat-icon>history</mat-icon>
                        <span class="truncate">{{ ev.title }}</span>
                      </a>
                    }
                  }
                }
              }

              @if (user.role === 'admin' && (menuItems.admin?.length ?? 0 > 0)) {
                <mat-divider class="!mt-2"></mat-divider>
                @if (cs.collapseMenu$ | async) {
                  <!-- Icon-only sidebar: no room for a header/toggle, show admin icons directly. -->
                  @for (m of menuItems.admin; track (m.url ?? m.label)) {
                    <nbd-nav-menu-item [collapsed]="true"
                                       [drawer]="drawer"
                                       [isHandset]="(hs.isHandset$ | async) ?? false"
                                       [m]="m"></nbd-nav-menu-item>
                  }
                } @else {
                  <!-- Collapsible admin section (default collapsed). -->
                  <button type="button" class="admin-subheader admin-toggle"
                          (click)="cs.adminExpanded$.next(!cs.adminExpanded$.value)">
                    <span>{{ 'core.navigation.adminSection' | translate }}</span>
                    <mat-icon class="admin-chevron"
                              [class.expanded]="(cs.adminExpanded$ | async) ?? false">expand_more</mat-icon>
                  </button>
                  @if (cs.adminExpanded$ | async) {
                    @for (m of menuItems.admin; track (m.url ?? m.label)) {
                      <nbd-nav-menu-item [collapsed]="false"
                                         [drawer]="drawer"
                                         [isHandset]="(hs.isHandset$ | async) ?? false"
                                         [m]="m"></nbd-nav-menu-item>
                    }
                  }
                }
              }
            </mat-nav-list>
          }
        </div>

        <!-- Help & guides quick link, pinned just above the footer so it's always
             reachable regardless of scroll position. Shows label + icon when expanded,
             icon-only (with a title tooltip) when the sidebar is collapsed. -->
        <a class="sidebar-help-link shrink-0" routerLink="/help" routerLinkActive="active"
           [class.collapsed]="cs.collapseMenu$ | async"
           [attr.title]="(cs.collapseMenu$ | async) ? ('core.navigation.help' | translate) : null"
           [attr.aria-label]="'core.navigation.help' | translate">
          <mat-icon>help_outline</mat-icon>
          @if (!(cs.collapseMenu$ | async)) {
            <span>{{ 'core.navigation.help' | translate }}</span>
          }
        </a>

        <!-- Sidebar footer: legal links + copyright + version (R4). Collapses to just
             the version in the icon-only sidebar where there's no room for the links. -->
        @if (cs.collapseMenu$ | async) {
          <div class="shrink-0 px-2 py-2 text-center border-t border-[var(--mat-sys-outline-variant)]">
            <span class="mat-label-small text-[var(--mat-sys-on-surface-variant)]">v{{ appVersion }}</span>
          </div>
        } @else {
          <div class="sidebar-footer shrink-0 border-t border-[var(--mat-sys-outline-variant)]">
            <div class="footer-links">
              <a routerLink="/legal/privacy">{{ 'core.navigation.privacy' | translate }}</a>
              <span aria-hidden="true">·</span>
              <a routerLink="/legal/terms">{{ 'core.navigation.terms' | translate }}</a>
            </div>
            <div class="footer-meta">© {{ year }} RealGuest · v{{ appVersion }}</div>
          </div>
        }
      </div>
    </mat-sidenav>
    <mat-sidenav-content class="!grid grid-cols-1 grid-rows-[max-content_1fr] !overflow-visible">
      <mat-toolbar color="primary">
        @if (hs.isHandset$ | async; ) {
          <button
            (click)="drawer.toggle()"
            aria-label="Toggle sidenav"
            mat-icon-button
            type="button">
            <mat-icon aria-label="Side nav toggle icon">menu</mat-icon>
          </button>
        }
        <nbd-toolbar class="ml-6"></nbd-toolbar>
        <ng-template toolbarMenuHost>
        </ng-template>

        <!-- Dark-mode toggle. The icon reflects the ACTION (moon = "switch to dark"),
             not the current state. ThemeService persists the choice + flips the
             --mat-sys-* tokens and the `.dark` class live. `ml-auto` pushes this and the
             user menu to the right end of the toolbar. -->
        <button mat-icon-button type="button" class="ml-auto shrink-0"
                (click)="theme.toggleDarkMode()"
                [matTooltip]="(theme.isDark() ? 'core.navigation.lightMode' : 'core.navigation.darkMode') | translate"
                [attr.aria-label]="(theme.isDark() ? 'core.navigation.lightMode' : 'core.navigation.darkMode') | translate">
          <mat-icon>{{ theme.isDark() ? 'light_mode' : 'dark_mode' }}</mat-icon>
        </button>

        <!-- C18: the user/account menu moved out of the sidebar to the top-right. -->
        <nbd-user-menu class="shrink-0"></nbd-user-menu>
      </mat-toolbar>
      <div #mainContent class="w-full h-full overflow-y-auto relative overflow-hidden">
        <router-outlet></router-outlet>
      </div>
    </mat-sidenav-content>
  </mat-sidenav-container>
} @else {
  <router-outlet></router-outlet>
}


@if (gl.globalLoader$ | async) {
  <div
    class="absolute w-full h-full top-0 left-0 [background-color:color-mix(in_srgb,var(--mat-sys-surface)_60%,transparent)]"
    style="z-index: 1000">
    <nbd-loader [fill]="true"></nbd-loader>
  </div>
}
